import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  useRef,
} from "react";
import { tx } from "@transifex/native";
import { TRANSIFEX_LIVE_API_KEY, TRANSIFEX_NATIVE_API_KEY } from "env-vars";
import { Language } from "../preferred-language";

declare global {
  interface Window {
    Transifex: {
      live: {
        loaded: boolean;
        translateTo: (lang: string) => void;
        onReady: (callback: () => void) => void;
        onAfterTranslate: (callback: () => void) => void;
        getSelectedLanguageCode: () => string;
        init: () => void;
      };
    };
    liveSettings: {
      api_key: string;
      manual_init: boolean;
      picker?: string | null;
    };
  }
}

type Props = {
  children: React.ReactNode;
};

export const TransifexLoaderContext = createContext<{
  lang: Language | null;
  error: boolean;
  isLoaded: boolean;
  setLang: (newLang: Language) => void;
}>({
  lang: null,
  error: false,
  isLoaded: false,
  setLang: () => {
    return;
  },
});

const getLocaleFromQueryParam = (): Language | null => {
  const param = new URLSearchParams(window.location.search);

  return param.get("locale") as Language | null;
};

const setLocaleInQueryParam = (locale: Language | null) => {
  const url = new URL(window.location.href);

  if (locale) {
    url.searchParams.set("locale", locale);
  } else {
    url.searchParams.delete("locale");
  }

  window.history.replaceState(window.history.state, "", url.toString());
};

/**
 * The "Live" Transifex project is used for the DOM-based
 * translations.
 * The "Native" Transifex project is used for dynamic
 * translations using the Transifex Native & React SDK.
 *
 * We need to switch both the Live and Native project languages
 * when the user updates their preferred language.
 */

export function TransifexLoaderProvider({ children }: Props) {
  const [error, setError] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [lang, setLang] = useState<Language | null>(getLocaleFromQueryParam());
  const transifexInitializedRef = useRef<boolean>(false);

  const handleLoad = useCallback(() => {
    const loaded = Boolean(window.Transifex?.live?.loaded);

    if (!loaded) {
      console.error("Transifex Live not loaded");
      return;
    }

    window.Transifex.live.init();

    setLoaded(loaded);

    // load initial language from query param
    if (lang) {
      window.Transifex.live.onReady(() => {
        console.log("Transifex Live: Language translate to", lang);

        window.Transifex.live.translateTo(lang);
        tx.setCurrentLocale(lang);
        transifexInitializedRef.current = true;
      });
    }

    // sync Transifex Live with the current language
    window.Transifex.live.onAfterTranslate(() => {
      // skip sync on initial load
      if (lang && !transifexInitializedRef.current) {
        return;
      }

      const currentLanguage = window.Transifex.live.getSelectedLanguageCode();

      console.log("Transifex Live: Language changed to", currentLanguage);
      tx.setCurrentLocale(currentLanguage);
      setLang(currentLanguage as Language);
    });
  }, []);

  const handleLoadError = useCallback(() => {
    setError(true);
  }, []);

  useEffect(() => {
    if (!TRANSIFEX_LIVE_API_KEY) {
      console.error("Transifex Live API key not found");
      return;
    }

    window.liveSettings = {
      api_key: TRANSIFEX_LIVE_API_KEY,
      manual_init: true,
      picker: null,
    };

    const script = document.createElement("script");

    script.src = "//cdn.transifex.com/live.js";
    script.async = true;
    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleLoadError);
    document.head.appendChild(script);
  }, [handleLoad, handleLoadError]);

  useEffect(() => {
    if (!TRANSIFEX_NATIVE_API_KEY) {
      console.error("Native Transifex token not found");
      return;
    }

    tx.init({ token: TRANSIFEX_NATIVE_API_KEY });
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const currentLanguage = window.Transifex.live.getSelectedLanguageCode();

    if (lang && lang !== currentLanguage) {
      console.log("Language changed to", lang);

      window.Transifex.live.translateTo(lang);
      tx.setCurrentLocale(lang);
    }

    setLocaleInQueryParam(lang);
  }, [isLoaded, lang]);

  const contextProps = useMemo(
    () => ({
      lang,
      error,
      isLoaded,
      setLang,
    }),
    [error, isLoaded, lang, setLang]
  );

  return (
    <TransifexLoaderContext.Provider value={contextProps}>
      {isLoaded && children}
    </TransifexLoaderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTransifexLoader(lang?: Language) {
  const context = useContext(TransifexLoaderContext);

  if (!context) {
    throw new Error(
      "useTransifexLoader must be used within a TransifexLoaderProvider"
    );
  }

  const { setLang } = context;

  useEffect(() => {
    if (lang) {
      setLang(lang);
    }
  }, [lang, setLang]);

  return context;
}
